import * as React from "react";
//import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import Tooltip from "@mui/material/Tooltip";
import { TransitionProps } from "@mui/material/transitions";

import useDeviceTye from "../../../../custom-hooks/DeviceType";
import Heading from "../../../../component-lib/Atoms/heading/EventEHRHeading";
import {
  CSV_HEADERS_PATIENT_REPORTS,
  STATUS_FOR_GENDER_PATIENT_REPORT,
  STATUS_FOR_TYPE_OF_VISIT_VISIT_REPORT,
  STATUS_FOR_VISIT_REPORT,
  toOperationalPeriodWindow,
} from "../../ReportConstants";
import Table from "../../../../component-lib/Organisms/table/EventEHRTable";
//import { formatPhoneNumber } from "../../utils/phoneNumberFormatter";
import {
  destructStatusData,
  getStatusBackground,
  getStatusDotColor,
  getStatusTextColor,
} from "../../../../utils/statusConstants";
import { useDispatch } from "react-redux";
import { plusIcon, searchIcon } from "../../../../assets";
import moment from "moment";
import { componentKey } from "../../ReportScreenSlice";
import { useSelector } from "react-redux";
import { TEXT_CONSTANTS } from "../../../../utils/text-constants/textConstants";
import Box from "@mui/material/Box";
import Button from "../../../../component-lib/Molecules/button/Button";
import Input from "../../../../component-lib/Atoms/Input/Input";
import SelectDropdown from "../../../../component-lib/Atoms/Select/Select";
import { patientReports, visitReport } from "../../ReportScreenSaga";
import { getFacilities } from "../../../patient-screen/patientSaga";
import { componentKey as patientComponentKey } from "../../../patient-screen/patientSlice";
import { formatPhoneNumber } from "../../../../utils/phoneNumberFormatter";
import MenuDropdown from "../../../../component-lib/Molecules/menuDropdown/MenuDropdown";
import DatePicker from "../../../../component-lib/Atoms/DatePicker/DatePicker";
import { CSVLink } from "react-csv";
import GetAppIcon from "@mui/icons-material/GetApp";

// Format per-patient diagnoses / orders / medications arrays into a single
// display string (reused by both the table rows and the CSV export).
const formatDiagnoses = (diagnoses: any[]) =>
  diagnoses?.length
    ? diagnoses
        .map((d: any) => d?.code || d?.description)
        .filter(Boolean)
        .join("\n")
    : "-";

// Group orders by type so repeated types (e.g. multiple "Rx Order"s) are merged
// into a single line listing all their items.
const formatOrders = (orders: any[]) => {
  if (!orders?.length) return "-";
  const byType: Record<string, string[]> = {};
  orders.forEach((o: any) => {
    const type = o?.type?.split("_")?.join(" ") || "Order";
    const items =
      o?.item?.map((i: any) => i?.name || i?.medicationName)?.filter(Boolean) ??
      [];
    (byType[type] ||= []).push(...items);
  });
  return Object.entries(byType)
    .map(([type, items]) =>
      items.length ? `${type}: ${items.join(", ")}` : type,
    )
    .join("\n");
};

const formatMedications = (medications: any[]) =>
  medications?.length
    ? medications
        .map((m: any) => {
          const qty = m?.quantity ? `x${m.quantity}` : "";
          const freq = m?.frequency
            ? qty
              ? `, ${m.frequency}`
              : m.frequency
            : "";
          const detail = qty || freq ? ` (${qty}${freq})` : "";
          return `${m?.medicationName || "-"}${detail}`;
        })
        .join("\n")
    : "-";

// Array variants of the above (one entry per item) — used by the table cells
// which show the first item as a chip and the rest in a tooltip.
const diagnosesList = (diagnoses: any[]): string[] =>
  (diagnoses || []).map((d: any) => d?.code || d?.description).filter(Boolean);

const ordersList = (orders: any[]): string[] => {
  if (!orders?.length) return [];
  const byType: Record<string, string[]> = {};
  orders.forEach((o: any) => {
    const type = o?.type?.split("_")?.join(" ") || "Order";
    const items =
      o?.item?.map((i: any) => i?.name || i?.medicationName)?.filter(Boolean) ??
      [];
    (byType[type] ||= []).push(...items);
  });
  return Object.entries(byType).map(([type, items]) =>
    items.length ? `${type}: ${items.join(", ")}` : type,
  );
};

const medicationsList = (medications: any[]): string[] =>
  (medications || []).map((m: any) => {
    const qty = m?.quantity ? `x${m.quantity}` : "";
    const freq = m?.frequency ? (qty ? `, ${m.frequency}` : m.frequency) : "";
    const detail = qty || freq ? ` (${qty}${freq})` : "";
    return `${m?.medicationName || "-"}${detail}`;
  });

// Shared chip cell: first item as a chip + a "+N" chip when there are more;
// hovering shows the full list, one per line.
const CHIP_BASE: React.CSSProperties = {
  borderRadius: "46px",
  padding: "2px 10px",
  fontSize: "12px",
  lineHeight: "18px",
  whiteSpace: "nowrap",
  display: "inline-block",
};

const renderChipCell = (items: string[]) => {
  if (!items?.length) {
    return <span className="text-neutral-90 font-normal">-</span>;
  }
  const moreCount = items.length - 1;
  const cell = (
    <div className="flex items-center gap-2">
      <span
        style={{
          ...CHIP_BASE,
          background: "#EEF2F6",
          color: "#475569",
          maxWidth: "220px",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {items[0]}
      </span>
      {moreCount > 0 && (
        <span style={{ ...CHIP_BASE, background: "#EAF2FB", color: "#005596" }}>
          +{moreCount}
        </span>
      )}
    </div>
  );
  return (
    <Tooltip
      arrow
      placement="bottom-start"
      slotProps={{ tooltip: { sx: { maxWidth: 580 } } }}
      title={<span style={{ whiteSpace: "pre-line" }}>{items.join("\n")}</span>}
    >
      {cell}
    </Tooltip>
  );
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

interface FullScreenPatientDialogProps {
  open: boolean;
  handleClose: () => void;
  patientFilterData: any;
  setPatientFilterData: any;
  selectedReport: string;
  isFilterVisible: any;
  setIsFilterVisible: any;
}

const FullScreenPatientDialog: React.FC<FullScreenPatientDialogProps> = ({
  open,
  handleClose,
  selectedReport,
  patientFilterData,
  setPatientFilterData,
  isFilterVisible,
  setIsFilterVisible,
}) => {
  const { patientReportsState, allPatientReport, patientReportMeta } =
    useSelector((state: any) => state[componentKey]);
  const { facilityData } = useSelector(
    (state: any) => state[patientComponentKey],
  );
  const deviceType = useDeviceTye();
  const dispatch = useDispatch();

  // Fetch the full facility list (limit 100, paginated) for the facility filter.
  React.useEffect(() => {
    dispatch(getFacilities());
  }, []);

  const facilityOptions = (facilityData ?? []).map((facility: any) => ({
    label: facility?.name,
    value: facility?.id,
  }));

  const adjustedGrid =
    deviceType === "tablet" || deviceType === "small-tablet"
      ? "1fr 1fr"
      : deviceType === "mobile"
        ? "1fr"
        : "1fr 1fr 1fr 1fr";

  const headerStyle = {
    fontWeight: "500",
    fontSize: "16px",
    paddingBottom: "10px",
  };

  const commonGridStyle = {
    gridTemplateColumns: adjustedGrid,
    padding: "0px 0px 12px 0px",
    display: "grid",
    gap: "24px 21px",
  };

  const [rowPerPage, setRowPerPage] = React.useState("10");

  const [pageNumber, setPageNumber] = React.useState(1);

  const [isFilterApplied, setIsFilterApplied] = React.useState({
    isTrue: false,
    filterData: {},
  });
  const [openConfirmationDialog, setOpenConfirmationDialog] =
    React.useState(false);
  const [csvData, setCsvData] = React.useState<any[]>([]);

  const [error, setError] = React.useState<any>("");

  const csvLinkRef = React.useRef<any>(null);

  // Auto-download the CSV only in response to a Download click — not when the
  // effect re-runs on mount against stale redux download data.
  const isDownloadClickedRef = React.useRef(false);

  const isFormFilled = Object.values(patientFilterData).some(
    (value) => value !== "" && value !== undefined,
  );

  React.useEffect(() => {
    try {
      const payload = {
        limit: rowPerPage,
        pageNumber: pageNumber,
        isReport: true,
        ...isFilterApplied?.filterData,
      };
      dispatch(patientReports(payload));
    } catch (error) {
      console.log(error);
    }
  }, [rowPerPage, pageNumber, isFilterApplied?.filterData]);

  // Handle applying filter
  const handleApplyFilter = (values: any) => {
    setPageNumber(1);
    let hasError = false;
    // The operational-period filter (single date -> 0700-0700 ET) is mutually
    // exclusive with the From/To range and needs none of the range validation.
    const isOperationalPeriod = Boolean(values?.operationalPeriod);
    const isFilteringByDateOnly =
      !isOperationalPeriod && (values?.startDate || values?.endDate);

    // Validation for Date Fields
    if (isFilteringByDateOnly) {
      if (!values?.startDate || !values?.endDate) {
        if (!values?.startDate && !values?.endDate) {
          setError("From Date and To Date are required.");
        } else if (!values?.startDate) {
          setError("From Date is required.");
        } else if (!values?.endDate) {
          setError("To Date is required.");
        }
        hasError = true;
      }
    }

    // Ensure endDate is greater than startDate
    if (!isOperationalPeriod && !hasError && values?.endDate < values?.startDate) {
      setError("To Date should be greater than Start Date.");
      hasError = true;
    }

    if (hasError) return;

    setError(null);

    let patientPayload: any = {};

    if (values.visitType && values.visitType.length !== "") {
      patientPayload.visitType = values.visitType;
    }
    if (values.status && values.status.length !== "") {
      patientPayload.status = values.status;
    }
    if (values.gender && values.gender.length !== "") {
      patientPayload.gender = values.gender;
    }
    if (isOperationalPeriod) {
      // IAP operational period: 0700 (Eastern) on the chosen date to 0700 the
      // next day, as UTC ISO instants. Anchored to Eastern so the window is the
      // same regardless of who runs the report.
      const { startDate, endDate } = toOperationalPeriodWindow(
        values.operationalPeriod,
      );
      patientPayload.startDate = startDate;
      patientPayload.endDate = endDate;
    } else {
      // Send UTC ISO instants anchored to the user's local day so the backend
      // filters the correct calendar day regardless of server timezone.
      if (values.startDate) {
        patientPayload.startDate = moment(values.startDate)
          .startOf("day")
          .toISOString();
      }
      if (values?.endDate) {
        patientPayload.endDate = moment(values.endDate)
          .endOf("day")
          .toISOString();
      }
    }
    if (values?.facilityId) {
      patientPayload.facilityId = values.facilityId;
    }

    setIsFilterVisible(true);
    setIsFilterApplied({ isTrue: true, filterData: { ...patientPayload } });
  };

  const handleClearFilter = () => {
    setPageNumber(1);
    setIsFilterVisible(false);
    setIsFilterApplied({ isTrue: false, filterData: {} });
    setError("");
  };

  // VISIT TAB ROW DATA
  const patientData = patientReportsState?.map((data: any) => {
    return {
      patientId: data?.id,
      bsaId: data?.participant?.bsaId || "-",
      patientName:
        `${data?.participant?.firstName} ${data?.participant?.lastName}` || "-",
      gender: data?.participant?.gender || "-",
      visitType:
        data?.visitType === "EMS_Visit"
          ? "EMS"
          : data?.visitType === "WALK_In_Visit"
            ? "Walk-In"
            : "-",
      baseCamp: data?.participant?.baseCamp?.unitNumber || "-",
      status: data?.status || "-",
      contactNumber: data?.participant?.contactNumber || "-",
      emergencyContact:
        data?.participant?.emergencyContact?.[0]?.contactNumber || "-",
      diagnoses: formatDiagnoses(data?.diagnoses),
      orders: formatOrders(data?.orders),
      medications: formatMedications(data?.medications),
      diagnosesList: diagnosesList(data?.diagnoses),
      ordersList: ordersList(data?.orders),
      medicationsList: medicationsList(data?.medications),
    };
  });

  // PATIENT TAB COLUMN DATA
  const patientColumns = [
    {
      field: "bsaId",
      label: <div>BSA ID</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal common-text-ellipse-style">
            {row?.bsaId}
          </p>
        );
      },
    },
    {
      field: "patientName",
      label: <div>Patient Name</div>,
      renderLogic: (row: any) => {
        return (
          <p
            className="font-medium text-primary-main cursor-pointer"
            // onClick={() => {
            //   localStorage.removeItem("selectedTab");
            //   navigate(`/patient/${row?.patientId}`);
            // }}
          >
            {row?.patientName}
          </p>
        );
      },
    },
    {
      field: "gender",
      label: <div>Gender</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.gender}</p>;
      },
    },
    {
      field: "visitType",
      label: <div>Visit Type</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.visitType}</p>;
      },
    },
    {
      field: "clinicLocation",
      label: <div>Unit Number</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.baseCamp}</p>;
      },
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: any) => {
        return (
          <div className="text-neutral-90 font-normal flex justify-start gap-1 items-center">
            <span
              className="font-weight-400 font-size-12px flex gap-1 items-center"
              style={{
                color: getStatusTextColor(row?.status),
                background: getStatusBackground(row?.status),
                borderRadius: "46px",
                padding: "4px 12px",
              }}
            >
              <span
                className="w-2 h-2"
                style={{
                  background: getStatusDotColor(row?.status),
                  borderRadius: "50%",
                }}
              ></span>
              {destructStatusData(row?.status)}
            </span>
          </div>
        );
      },
    },
    {
      field: "contactNumber",
      label: <div>Contact Number</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {formatPhoneNumber(row?.contactNumber)}
          </p>
        );
      },
    },
    {
      field: "emergencyContact",
      label: <div>Emergency Contact</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {formatPhoneNumber(row?.emergencyContact)}
          </p>
        );
      },
    },
    // {
    //   field: "diagnoses",
    //   label: <div>Diagnoses</div>,
    //   renderLogic: (row: any) => renderChipCell(row?.diagnosesList),
    // },
    // {
    //   field: "orders",
    //   label: <div>Orders</div>,
    //   renderLogic: (row: any) => renderChipCell(row?.ordersList),
    // },
    // {
    //   field: "medications",
    //   label: <div>Medications</div>,
    //   renderLogic: (row: any) => renderChipCell(row?.medicationsList),
    // },
    // {
    //   field: "action",
    //   label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
    //   renderLogic: (row: any) => {
    //     return (
    //       <>
    //         <div className="flex items-center">
    //           <MenuDropdown
    //             options={[
    //               {
    //                 label: "View Patient",
    //                 onClickCb: () => {
    //                   // localStorage.removeItem("selectedTab");
    //                   // navigate(`/patient/${row?.patientId}`);
    //                 },
    //               },
    //             ]}
    //           />
    //         </div>
    //       </>
    //     );
    //   },
    // },
  ];

  const downloadPatientReportHandler = (action: string) => {
    if (action === "close") {
      setOpenConfirmationDialog(false);
    } else if (action === "submit") {
      isDownloadClickedRef.current = true;
      dispatch(
        patientReports({
          limit: "All",
          isReport: true,
          ...isFilterApplied?.filterData,
        }),
      );
    }
  };

  React.useEffect(() => {
    if (
      isDownloadClickedRef.current &&
      allPatientReport &&
      allPatientReport?.patient?.length > 0
    ) {
      const formattedPatientReportData =
        allPatientReport?.patient?.map((data: any) => ({
          patientId: data?.id,
          bsaId: data?.participant?.bsaId || "-",
          patientName:
            `${data?.participant?.firstName} ${data?.participant?.lastName}` ||
            "-",
          gender: data?.participant?.gender || "-",
          visitType:
            data?.visitType === "EMS_Visit"
              ? "EMS"
              : data?.visitType === "WALK_In_Visit"
                ? "Walk-In"
                : "-",
          baseCamp: data?.participant?.baseCamp?.unitNumber || "-",
          status: data?.status || "-",
          contactNumber: data?.participant?.contactNumber || "-",
          emergencyContact:
            data?.participant?.emergencyContact?.[0]?.contactNumber || "-",
          diagnoses: formatDiagnoses(data?.diagnoses),
          orders: formatOrders(data?.orders),
          medications: formatMedications(data?.medications),
        })) ?? [];

      if (formattedPatientReportData?.length > 0) {
        setCsvData(formattedPatientReportData);
        setTimeout(() => {
          csvLinkRef.current?.link.click();
        }, 500);
      }
      isDownloadClickedRef.current = false;
    }
  }, [allPatientReport]);

  return (
    <>
      <Dialog
        fullScreen
        open={open}
        onClose={setPatientFilterData}
        TransitionComponent={Transition}
        sx={{
          backgroundColor: "white",
          minWidth: "200px",
          margin: "3px",
          marginTop: "50px",
          padding: "20px",
          height: "calc(100dvh - 97px)",
        }}
      >
        <AppBar sx={{ position: "relative", backgroundColor: "#005596" }}>
          <Toolbar>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {selectedReport}
            </Typography>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 2, overflow: "auto" }}>
          {/* <p className="text-xl">
          Use the filters above to narrow down your results. Select one or more
          options to find exactly what you're looking for
        </p> */}
          <br />
          {/* //MAIN CODE */}
          <div>
            {/* //FILTER CODE */}
            <div
              style={{ border: "1px solid #D2D2D2" }}
              className="py-4 px-3 rounded mb-6"
            >
              <div className="w-full flex flex-col gap-6 rounded-lg">
                {/* PATIENT DETAILS */}
                <div>
                  <div className="flex justify-between mb-2">
                    <Heading title="Filter" customStyle={headerStyle} />
                    <div onClick={handleClose} className="cursor-pointer">
                      {" "}
                      {/* <CloseIcon /> */}
                    </div>
                  </div>
                  <div style={commonGridStyle}>
                    {/* Task Title Auto Select */}
                    <SelectDropdown
                      label="Visit Type"
                      placeholderValue="All Visit Type"
                      placeholderDisable={false}
                      name={"type"}
                      value={patientFilterData?.visitType}
                      options={STATUS_FOR_TYPE_OF_VISIT_VISIT_REPORT}
                      onChangeCb={(e) => {
                        setPatientFilterData((prevData: any) => ({
                          ...prevData,
                          visitType: e.target.value,
                        }));
                      }}
                    />
                    <SelectDropdown
                      placeholderValue="All Facilities"
                      placeholderDisable={false}
                      displayEmpty={true}
                      name={"facilityId"}
                      value={patientFilterData?.facilityId}
                      options={facilityOptions}
                      onChangeCb={(e) => {
                        setPatientFilterData((prevData: any) => ({
                          ...prevData,
                          facilityId: e.target.value,
                        }));
                      }}
                    />
                    {/* IAP operational period: single date -> 0700-0700 ET.
                        Mutually exclusive with the From/To range below. */}
                    <DatePicker
                      name={`Operational Period`}
                      value={patientFilterData?.operationalPeriod}
                      onChangeCb={(value) => {
                        setPatientFilterData((prevData: any) => ({
                          ...prevData,
                          operationalPeriod: value,
                        }));
                      }}
                      label="Operational Period (0700–0700 ET)"
                      style={{ width: "100%" }}
                      maxDate={moment()}
                      disabled={
                        !!patientFilterData?.startDate ||
                        !!patientFilterData?.endDate
                      }
                    />
                    <DatePicker
                      name={`From Date`}
                      value={patientFilterData?.startDate}
                      onChangeCb={(value) => {
                        setPatientFilterData((prevData: any) => ({
                          ...prevData,
                          startDate: value,
                        }));
                      }}
                      label="From Date"
                      style={{ width: "100%" }}
                      maxDate={moment()}
                      disabled={!!patientFilterData?.operationalPeriod}
                    />
                    <DatePicker
                      name={patientFilterData?.endDate}
                      value={patientFilterData?.endDate}
                      onChangeCb={(value) => {
                        setPatientFilterData((prevData: any) => ({
                          ...prevData,
                          endDate: value,
                        }));
                      }}
                      label="To Date"
                      style={{ width: "100%" }}
                      maxDate={moment()}
                      disabled={!!patientFilterData?.operationalPeriod}
                    />
                    <SelectDropdown
                      label="Gender"
                      placeholderValue="All Gender"
                      placeholderDisable={false}
                      name={"gender"}
                      value={patientFilterData?.gender}
                      options={STATUS_FOR_GENDER_PATIENT_REPORT}
                      onChangeCb={(e) => {
                        setPatientFilterData((prevData: any) => ({
                          ...prevData,
                          gender: e.target.value,
                        }));
                      }}
                    />
                    <SelectDropdown
                      label="Visit Status"
                      placeholderValue="All Status"
                      placeholderDisable={false}
                      name={"status"}
                      value={patientFilterData?.status}
                      options={STATUS_FOR_VISIT_REPORT}
                      onChangeCb={(e) => {
                        setPatientFilterData((prevData: any) => ({
                          ...prevData,
                          status: e.target.value,
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-4">
                {error && (
                  <p
                    style={{
                      color: "red",
                      marginTop: "10px",
                      marginRight: "auto",
                    }}
                  >
                    {error}
                  </p>
                )}
                {isFilterVisible && (
                  <Button
                    startIcon={<GetAppIcon />}
                    label="Download Reports"
                    variant="contained"
                    onClickCb={() => {
                      downloadPatientReportHandler("submit");
                    }}
                    style={{
                      width: "200px",
                      height: "40px",
                      marginTop: "3px",
                    }}
                  />
                )}

                <Button
                  label={TEXT_CONSTANTS?.CLEAR_ALL}
                  variant="outlined"
                  onClickCb={() => {
                    setPatientFilterData({
                      patientName: "",
                      // assignTo: "",
                      // assignBy: "",
                      visitType: "",
                      startDate: undefined,
                      endDate: undefined,
                      operationalPeriod: undefined,
                      //date: undefined,
                      status: "",
                      gender: "",
                      facilityId: "",
                    });
                    // setDropdownData((prev) => ({
                    //   assignTo: { ...prev.assignTo, selectedValue: null },
                    //   assignBy: { ...prev.assignBy, selectedValue: null },
                    // }));
                    handleClearFilter();
                  }}
                  style={{
                    width: "120px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                    backgroundColor: "white",
                    border: "none",
                    color: "#005596",
                  }}
                  //variant={undefined}        />
                />
                <Button
                  type="submit"
                  label={TEXT_CONSTANTS?.APPLY}
                  variant={isFormFilled ? "outlined" : "contained"}
                  disabled={!isFormFilled}
                  onClickCb={() => {
                    handleApplyFilter(patientFilterData);
                  }}
                  style={{
                    background: isFormFilled ? "#E4F3FF" : "",
                    color: isFormFilled ? "#005596" : "",
                    width: "120px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                  }}
                ></Button>
              </div>
            </div>
            {isFilterVisible && (
              <div>
                <Table
                  columns={patientColumns}
                  rows={patientData}
                  paginationProps={{
                    isPagination: true,
                    page: pageNumber,
                    totalPages: patientReportMeta?.totalPages,
                    totalCount: patientReportMeta?.total,
                    limit: rowPerPage,
                    rowsPerPageOptions: [10, 25, 50],
                    onPageChange: (page: any) => {
                      setPageNumber(page);
                    },
                    handleChangeRowsPerPage: (rowPerPage: any) => {
                      setRowPerPage(rowPerPage);
                      setPageNumber(1);
                    },
                  }}
                  noDataFoundMessage={"No tasks found"}
                />
              </div>
            )}
          </div>
        </Box>
      </Dialog>
      {
        <CSVLink
          data={csvData}
          headers={CSV_HEADERS_PATIENT_REPORTS}
          ref={csvLinkRef}
          style={{ display: "none" }}
        />
      }
    </>
  );
};

export default FullScreenPatientDialog;
